import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, X, Loader2, Clapperboard, Users, Inbox, Film, Lightbulb, LibraryBig, CornerDownLeft } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

const GROUP_META = {
  jobs: { label: "Jobs", icon: Clapperboard },
  team: { label: "Team", icon: Users },
  applications: { label: "Applications", icon: Inbox },
  videos: { label: "Videos", icon: Film },
  ideas: { label: "Ideas", icon: Lightbulb },
  competitors: { label: "Competitors", icon: LibraryBig },
  assets: { label: "Assets", icon: LibraryBig },
};

// Global Ctrl+K / Cmd+K command palette — searches the whole operation and jumps to sections.
export const CommandPalette = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setResults({}); }
  }, [open]);

  const search = useCallback((query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setResults({}); setBusy(false); return; }
    setBusy(true);
    debounceRef.current = setTimeout(() => {
      axios.get(`${API}/staff/search?q=${encodeURIComponent(query)}`, { headers: authHeaders() })
        .then((r) => setResults(r.data.results || {}))
        .catch(() => {})
        .finally(() => setBusy(false));
    }, 250);
  }, []);

  const go = (item) => {
    onNavigate?.(item.section);
    setOpen(false);
  };

  const hasResults = Object.values(results).some((arr) => arr.length > 0);

  if (!open) return null;
  return (
    <div data-testid="command-palette" className="fixed inset-0 z-[80] flex items-start justify-center bg-black/60 pt-24 backdrop-blur-sm" onClick={() => setOpen(false)}>
      <div className="w-full max-w-xl overflow-hidden rounded-3xl border border-[var(--pv-border-strong)] bg-[var(--pv-bg)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-[var(--pv-border)] px-5 py-4">
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-cyan-400" /> : <Search className="h-4 w-4 text-cyan-400" />}
          <input
            ref={inputRef}
            data-testid="command-palette-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
            placeholder="Search jobs, people, applications, videos, ideas…"
            className="flex-1 bg-transparent text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:outline-none"
          />
          <button data-testid="command-palette-close" onClick={() => setOpen(false)} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-96 overflow-y-auto p-3">
          {q.trim().length < 2 ? (
            <p className="px-3 py-8 text-center text-xs text-[var(--pv-text-4)]">Type at least 2 characters to search everything.</p>
          ) : !busy && !hasResults ? (
            <p data-testid="command-palette-empty" className="px-3 py-8 text-center text-xs text-[var(--pv-text-4)]">No matches for "{q}".</p>
          ) : (
            Object.entries(GROUP_META).map(([key, meta]) => {
              const items = results[key] || [];
              if (!items.length) return null;
              return (
                <div key={key} data-testid={`palette-group-${key}`} className="mb-2">
                  <p className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">
                    <meta.icon className="h-3 w-3" /> {meta.label}
                  </p>
                  {items.map((item) => (
                    <button
                      key={item.id}
                      data-testid={`palette-result-${key}-${item.id}`}
                      onClick={() => go(item)}
                      className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[var(--pv-subtle)]"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-[var(--pv-text)]">{item.title}</span>
                        {item.status && <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{String(item.status).replace(/_/g, " ")}</span>}
                      </span>
                      <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-[var(--pv-text-4)]" />
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
        <div className="border-t border-[var(--pv-border)] px-5 py-2.5 text-[10px] font-semibold text-[var(--pv-text-4)]">
          Ctrl+K to toggle · Esc to close
        </div>
      </div>
    </div>
  );
};
